import { Subject, ReplaySubject, BehaviorSubject } from 'rxjs';
import { share } from 'rxjs/operators';

import { DashboardService, CampaignService, AuthService } from '@/services';

import {
    isValueExist,
    changeOrder,
    addIdUnless,
    indexById,
    notEq,
    noValueExist,
    getFrozenEmptyObj,
    defaultToFrozenEmptyObj,
    toPixel,
} from '@util';
import {
    getUpdateViewSyncedFromDashboardToReportMode,
    getUpdateViewSyncedFromReportToDashboard,
} from '@/utils/reportMode';

import {
    VIEW_DEFAULT_DATE_FILTR,
    WIDGET_ADD_TYPE,
    EMPTY_OBJ,
    WIDGET_TYPES,
} from '@consts';

import {
    makeDimension,
    setWidgetInsideView,
    STORAGE_TYPES,
    parseTagsToParam,
    isView,
    isFolder,
    isMenu,
    FLATTEN_VIEWS,
    VIEW_PATH_MAP,
    updateList,
    convertRelativeDate,
    getDateFilter,
    getComparisonDateFilter,
    leftClamp,
    whenFolder,
    FLATTEN_LIST,
    getCellType,
    getMergedDashboardFilter,
    clearEmptyFilter,
    refineViewLayout,
    CONNECTION_LOST,
    ERROR_TYPE,
    checkIsAnalyticSource,
    DATE_GROUP_ROWS,
    checkIsView,
    getAdvancedDateFilter,
    isAdvancedDateFilter,
    refineChannel,
    checkIsHidden,
} from '@/utils/dashboard';

import { getDateGroupUnit, getDefaultDate } from '@/utils/timeUtils';

import { NOTI_TYPES } from '@/store/dashboardEdit';

import memoize from 'lodash/memoize';
import moment from 'moment';
import { makeFilterFromFolderWhere } from '@/utils/filter';
import {
    getAccountKey,
    getAccountKeyWithDisplayChannelName,
} from '@/components/Dashboard/common/WidgetSettingPanel';

const { SUCCESS: NOTI_SUCCESS, FAIL: NOTI_FAIL } = NOTI_TYPES;

const addTemplateOrFirst = (views = []) => {
    views = R.reject(isFolder, FLATTEN_VIEWS(views));
    views = R.reject(R.propEq('isViewable', false), views);
    views = R.reject(R.propEq('isDummy', true), views);

    const fView = views[0] || { id: 'addTemplate' };

    return fView.id;
};

export const ADD_NOTI = 'view/addNotiMsg';

const aggregateObj = memoize(
    (prev, next) => ({ ...prev, ...next }),
    (prev, next) => JSON.stringify(prev) + '|' + JSON.stringify(next)
);

const exportTypes = memoize(
    R.compose(R.pluck('type'), R.indexBy(R.prop('name'))),
    (headers = []) => headers.join('')
);

function getDefaultState() {
    return {
        campaignId: null,
        campaignIdList: [],
        gridCheckMap: getFrozenEmptyObj(),
        uniqueChannels: [],
        dimensions: undefined,
        selectedTab: 'addTemplate',
        views: undefined,
        viewCreated$: new BehaviorSubject({}),
        contentScroll$: new Subject().pipe(share()),
        editingWidget: null,
        isDateFilterVisible: false,
        shouldCloseAllSecondPanel: false,
        tableFetched$: new Subject(),
        apiConfig$: new ReplaySubject(1),
        grids: getFrozenEmptyObj(),
        tableAdAccounts: undefined,
        teamspaceAllowedChannelIds: undefined,
        teamspaceAllowedAssetIds: undefined,
        originViews: {},
        widgetError: getFrozenEmptyObj(),
        widgetAdded$: new Subject().pipe(share()),
        compareApiConfig$: new ReplaySubject(1),
        selectedDataSources$: new Subject(),
        headerPanelLoading: {},
        widgetStatusPanelWidgetID: null,
        previewCreative: {},
        lastUpdatedMap: {},
        fullScreenWidgetId: '',
        leftNavWidth: undefined,
        forceTrigger$: new ReplaySubject(1),
        isPrinting: false,
        listUnfolded: {},
        listExpanded: {},
        customMetrics: [],
        editingFolderId: null,
        gridLayoutRef: null,
        reportModeGridLayoutRefs: {},
        isShareMode: false,
        leftNavFolded: false,
        ignoreSave: false,
        mediaJwtToken: undefined,
        metricTypeMap: {},
        isTakeSnapshotLoading: false,
        metricAliases: [],

        // TODO: derived from new dataSources API
        dataSources: undefined,
        hasNoDataSources: false,

        // top button
        showTopBtn: true,

        mcfBreakdowns: [],
        hubspotBannerHeight: null,

        currentWidgetTitlesById: {},

        isMemberSelectorPanelOpen: false,

        // goal range setting dialog
        goalRangeSettingData: {},

        // metric / breakdown / custom breakdown(from list filter widget) panel
        metricPanelData: null,
        breakdownPanelData: null,
        customBreakdownPanelData: null,

        isBreakdownFilterLoading: false,

        // filter panel
        isWidgetFilterOpen: false,
    };
}

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        setMcfBreakdowns(state, breakdowns) {
            state.mcfBreakdowns = breakdowns;
        },
        setHubspotBannerHeight(state, height) {
            state.hubspotBannerHeight = height;
        },
        setShowTopBtn(state, payload) {
            state.showTopBtn = payload;
        },
        setIsMemberSelectorPanelOpen(state, payload) {
            state.isMemberSelectorPanelOpen = payload;
        },
        setGoalRangeSettingData(state, payload) {
            state.goalRangeSettingData = payload;
        },
        setMetricPanelData(state, payload) {
            state.metricPanelData = payload;
        },
        setBreakdownPanelData(state, payload) {
            state.breakdownPanelData = payload;
        },
        setCustomBreakdownPanelData(state, payload) {
            state.customBreakdownPanelData = payload;
        },
        setIsWidgetFilterOpen(state, payload) {
            state.isWidgetFilterOpen = payload;
        },
        setDataSources(state, payload) {
            const { dataSources } = payload;
            if (dataSources && dataSources.length < 1) {
                state.hasNoDataSources = true;
            }
            state.dataSources = dataSources;
        },
        setWidgetStatusPanelWidgetID(state, value) {
            state.widgetStatusPanelWidgetID = value;
        },
        setCampaignId(state, id) {
            state.campaignId = id;
        },
        setCampaignIdList(state, list) {
            state.campaignIdList = list;
        },
        clear(state) {
            // maintain views
            Object.assign(
                state,
                R.omit(['views', 'originViews'], getDefaultState())
            );
        },
        // type ADD | DELETE | CLEAR
        handleGridCheckMap(state, { gridId, tableIds = [], type = 'ADD' }) {
            let _set = new Set(state.gridCheckMap[gridId] || []);

            switch (type) {
                case 'ADD':
                    tableIds.forEach((id) => _set.add(id));
                    break;
                case 'DELETE':
                    tableIds.forEach((id) => _set.delete(id));
                    break;
                case 'CLEAR':
                    _set = new Set();
                    break;
            }
            let map = R.assoc(gridId, _set, state.gridCheckMap);
            map = R.filter(R.prop('size'), map);

            if (!isValueExist(map) && !isValueExist(state.gridCheckMap)) {
                return;
            }

            state.gridCheckMap = Object.freeze(map);
        },
        setDimensions(state, dimensions = []) {
            const refined = R.compose(
                R.sortBy(R.compose(R.toLower, R.prop('name'))),
                refineDimensions,
                R.filter(R.prop('id'))
            )(dimensions);

            state.dimensions = Object.freeze(refined);
        },
        setSelectedTab(state, value) {
            value = String(value);
            state.selectedTab = value;
        },
        setViews(state, views = []) {
            views = refineViews(views);
            views = attachTeamspaceName(views);
            views = sortByOrder(views);

            if (R.equals(views, state.views)) {
                return;
            }
            state.views = Object.freeze(views);
        },
        updateView(state, view) {
            const { views } = state;

            let pathMap = VIEW_PATH_MAP({ data: views });

            view = refineView(view);
            let path = pathMap[view.id] || [];

            if (!isValueExist(path)) {
                return;
            }

            path = R.intersperse('children', path);

            const lens = R.lensPath(path);
            const oldView = R.view(lens, views);

            if (notEq(oldView, view)) {
                state.views = Object.freeze(
                    R.over(R.lensPath(path), R.always(view), views)
                );
            }
        },
        updateEditingWidget(state, widget) {
            if (noValueExist(widget) && noValueExist(state.editingWidget)) {
                return;
            }
            state.editingWidget = widget;
        },
        setDateFilterVisibility(state, value) {
            state.isDateFilterVisible = value;
        },
        setShouldCloseAllSecondPanel(state, value) {
            state.shouldCloseAllSecondPanel = value;
        },
        updateGrids(state, { grid, id, isFreezing }) {
            if (!id) {
                return;
            }

            const before = R.compose(
                R.indexBy(R.prop('tableId')),
                FLATTEN_LIST,
                R.objOf('data'),
                R.defaultTo([])
            )(state.grids[id]);

            if (isValueExist(before)) {
                grid = updateList((v) => {
                    if (!isValueExist(v.children)) {
                        v.children = R.pathOr(
                            [],
                            [v.tableId, 'children'],
                            before
                        );
                    }

                    return v;
                }, grid);
            }

            if (isFreezing && grid) {
                grid = Object.freeze(grid);
            }

            state.grids = Object.freeze(R.assoc(id, grid, state.grids));
        },
        setTableAdAccounts(state, value) {
            state.tableAdAccounts = Object.freeze(value);
        },
        setTeamspaceAllowedChannelIds(state, value) {
            state.teamspaceAllowedChannelIds = Object.freeze(value);
        },
        setTeamspaceAllowedAssetIds(state, value) {
            state.teamspaceAllowedAssetIds = Object.freeze(value);
        },
        setOriginViews(state, value = {}) {
            if (R.isNil(value)) {
                return;
            }

            const isArray = Array.isArray(value);

            let views = isArray ? [...value] : { ...value };

            if (isArray) {
                views = indexById(FLATTEN_VIEWS(value));
            }

            state.originViews = Object.freeze(views);
        },
        setWidgetError(state, { id, errorData, type = 'ADD' }) {
            if (!id) {
                return;
            }

            switch (type) {
                case 'ADD':
                    state.widgetError = Object.freeze(
                        R.assoc(id, errorData, state.widgetError)
                    );
                    break;
                case 'DELETE':
                    state.widgetError = Object.freeze(
                        R.dissoc(id, state.widgetError)
                    );
                    break;
            }
        },
        resetGrids(state) {
            if (isValueExist(state.grids)) {
                state.grids = Object.freeze({});
            }
        },
        mutateGridItem(state, updated) {
            if (!updated) {
                return;
            }

            // update all with same id
            const mergeWhen = R.when(
                R.propEq('id', updated.id),
                (prevValue) => {
                    const nextValue = R.mergeDeepLeft(updated, prevValue);

                    return R.equals(prevValue, nextValue)
                        ? prevValue
                        : nextValue;
                }
            );

            state.grids = Object.freeze(
                R.map(updateList(mergeWhen), R.propOr({}, 'grids', state))
            );
        },
        setHeaderPanelLoading(state, { id, value }) {
            const oldValue = state.headerPanelLoading;
            let newValue = R.assoc(id, value, oldValue);
            newValue = R.filter(R.identity, newValue);

            if (notEq(oldValue, newValue)) {
                state.headerPanelLoading = Object.freeze(newValue);
            }
        },
        setPreviewCreative(state, value) {
            if (notEq(state.previewCreative, value)) {
                state.previewCreative = value;
            }
        },
        setLastUpdatedMap(state, { id, value, type }) {
            if (!id) {
                return;
            }

            let func = R.identity;

            switch (type) {
                case 'ADD':
                    func = R.assoc(id, value);
                    break;
                case 'DELETE':
                    func = R.dissoc(id);
                    break;
            }

            const newValue = func(state.lastUpdatedMap);

            state.lastUpdatedMap = Object.freeze(newValue);
        },
        clearLastUpdatedMap(state) {
            if (isValueExist(state.lastUpdatedMap)) {
                state.lastUpdatedMap = Object.freeze({});
            }
        },
        setFullScreenWidgetId(state, id) {
            state.fullScreenWidgetId = id;
        },
        setLeftNavWidth(state, value) {
            value = leftClamp(parseFloat(value));

            if (state.leftNavWidth !== value) {
                state.leftNavWidth = value;
            }
        },
        setIsPrinting(state, value) {
            state.isPrinting = value;
        },
        updateListUnfolded(state, { widgetId, value }) {
            let nextValue = R.assoc(
                widgetId,
                new Set(value),
                state.listUnfolded
            );

            nextValue = R.filter(R.prop('size'), nextValue);

            if (!isValueExist(nextValue) && !isValueExist(state.listUnfolded)) {
                return;
            }

            state.listUnfolded = Object.freeze(nextValue);
        },
        updateListExpanded(state, { widgetId, value }) {
            state.listExpanded = Object.freeze(
                R.assoc(widgetId, new Set(value), state.listExpanded)
            );
        },
        setCustomMetrics(state, values = []) {
            values = R.compose(
                R.sortBy(R.compose(R.toLower, R.prop('name'))),
                R.map(
                    R.when(
                        R.propSatisfies(R.isNil, 'metrics'),
                        R.assoc('metrics', [])
                    )
                )
            )(values);

            state.customMetrics = Object.freeze(values);
        },
        reset(state) {
            Object.assign(state, getDefaultState());
        },
        setEditingFolderId(state, id) {
            state.editingFolderId = id;
        },
        setGridLayoutRef(state, value) {
            state.gridLayoutRef = value;
        },
        setReportModeGridLayoutRefs(state, value) {
            const { pageGridId, layoutRef, reset = false } = value;
            if (reset) {
                state.reportModeGridLayoutRefs = {};
            } else {
                state.reportModeGridLayoutRefs = R.assoc(
                    pageGridId,
                    layoutRef
                )(state.reportModeGridLayoutRefs);
            }
        },
        setIsShareMode(state, value) {
            state.isShareMode = value;
        },
        setLeftNavFolded(state, value) {
            state.leftNavFolded = value;
        },
        setIgnoreSave(state, value) {
            state.ignoreSave = value;
        },
        setMediaJwtToken(state, payload) {
            state.mediaJwtToken = payload.token;
        },
        setMetricTypeMap(state, value) {
            if (isValueExist(value)) {
                const refined = aggregateObj(
                    state.metricTypeMap,
                    exportTypes(value)
                );

                state.metricTypeMap = Object.freeze(refined);
            }
        },
        setIsTakeSnapshotLoading(state, value) {
            state.isTakeSnapshotLoading = value;
        },
        setMetricAliases(state, value) {
            state.metricAliases = value;
        },
        setCurrentWidgetTitlesById(state, value) {
            state.currentWidgetTitlesById = value;
        },
        setIsBreakdownFilterLoading(state, value) {
            state.isBreakdownFilterLoading = value;
        },
    },
    actions: {
        getMediaJwtToken({ commit }, payload = {}) {
            const { key, value } = payload;
            return AuthService.getJwtTokenWithHeader({
                key,
                value,
            }).then((data) => {
                const { mediaToken: token } = data;
                commit('setMediaJwtToken', {
                    token,
                });
            });
        },
        getPreview(_, queries) {
            return DashboardService.getPreview(queries).then((data) => data);
        },
        extendCampaign(_, { id, data }) {
            return CampaignService.extend(id, data)
                .then((data) => data)
                .catch((err) => console.error(err));
        },
        getDimensionList({ commit, rootGetters }) {
            const currentView = rootGetters['newDashboard/currentView'];
            const isHiddenTeamspace = checkIsHidden(currentView);

            const params = isHiddenTeamspace
                ? {}
                : {
                      viewId: R.prop('id', currentView),
                      teamspaceId: R.path(['teamspace', 'id'], currentView),
                  };

            return DashboardService.getCustomBreakdowns(params)
                .then((dimensions = []) => {
                    commit('setDimensions', dimensions);

                    return dimensions;
                })
                .catch(console.error);
        },
        createView(
            { dispatch },
            {
                subtype = 'normal',
                config = { dateFilter: VIEW_DEFAULT_DATE_FILTR },
                notify = true,
                parent_id,
                order,
                originalViewId,
                view_title,
            }
        ) {
            return dispatch('_createView', {
                config,
                notify,
                parent_id,
                order,
                type: 'view',
                subtype,
                originalViewId,
                view_title,
            });
        },
        // someday need specific logic for folder
        createFolder(
            { dispatch },
            { subtype = 'normal', config, notify = true, parent_id, order } = {}
        ) {
            return dispatch('_createView', {
                config,
                notify,
                parent_id,
                order,
                type: 'folder',
                subtype,
                mapper: R.assoc('$folded', true),
            });
        },
        _createView(
            { commit, state, rootGetters, getters },
            {
                config,
                notify = true,
                parent_id,
                order,
                type,
                subtype,
                mapper = R.identity,
                originalViewId,
                view_title,
            } = {}
        ) {
            const userAccountId = rootGetters['user/userAccountId'];

            return DashboardService.createView({
                type,
                subtype,
                config,
                userAccountId,
                parent_id,
                originalViewId,
                view_title,
            }).then((view) => {
                view = mapper(view);
                view.id = '' + view.id;

                if (parent_id) {
                    // case for nested
                    let pView = getters.viewsById[parent_id];

                    let children = pView.children || [];

                    if (order === undefined) {
                        order = children.length + 1;
                    }

                    if (!isMenu(view)) {
                        view.order = order;
                    }
                    children = R.insert(order, view, children);

                    commit('updateView', {
                        ...pView,
                        children,
                    });
                } else {
                    // case for root lvl
                    if (order === undefined) {
                        order = state.views.length;
                    }

                    view.order = order;
                    commit('setViews', R.insert(order, view, state.views));
                }

                // origin views doesn't need structure equality
                commit('setOriginViews', [
                    ...Object.values(state.originViews),
                    view,
                ]);

                notify === true && state.viewCreated$.next(view);

                return view;
            });
        },
        getViews({ commit, state, dispatch }, query = {}) {
            const { viewId, isForce } = query;
            query = R.omit(['viewId', 'isForce'], query);

            if (isForce !== true && isValueExist(state.views) && viewId) {
                dispatch('selectByViewId', { views: state.views, viewId });
                return Promise.resolve();
            }

            const transform = R.compose(
                R.map(R.compose(attachPermissions, attachFolded, refineView)),
                R.defaultTo([])
            );

            return DashboardService.getViews(query)
                .then(transform)
                .then((views) => {
                    viewId && dispatch('selectByViewId', { views, viewId });
                    commit('setViews', views);
                    commit('setOriginViews', state.views);
                });

            function attachFolded(view) {
                return R.when(
                    R.allPass([
                        isFolder,
                        R.propSatisfies(isValueExist, 'children'),
                    ]),
                    R.compose(
                        R.over(R.lensProp('children'), R.map(attachFolded)),
                        R.assoc('$folded', true)
                    )
                )(view);
            }
        },
        deleteView({ commit, state, rootGetters }, { id }) {
            id = String(id);

            const userAccountId = rootGetters['user/userAccountId'];

            return DashboardService.deleteView({ id, userAccountId }).then(
                () => {
                    const stateViews = state.views;
                    let newViews = [];

                    let pathMap = VIEW_PATH_MAP({ data: stateViews });
                    let path = pathMap[id] || [];

                    if (!isValueExist(path)) {
                        return;
                    }

                    path = R.intersperse('children', path);
                    newViews = R.over(
                        R.lensPath(R.dropLast(1, path)),
                        R.remove(R.last(path), 1),
                        stateViews
                    );

                    commit('setViews', newViews);

                    commit('setSelectedTab', addTemplateOrFirst(newViews));
                }
            );
        },
        // type store | server
        saveView(
            { commit, rootGetters, state, getters, rootState },
            {
                view,
                type = STORAGE_TYPES.STORE,
                silent = false,
                isAutoSave = false,
                // TODO: added this arg to focus on if it is a text widget
                widget = undefined,
            }
        ) {
            if (!view.id) {
                return;
            }

            const userAccountId = rootGetters['user/userAccountId'];

            /**
             * @note Temporary codes to remove empty widget type
             */
            // const refinedWidgets = R.compose(
            //     R.filter(R.compose(R.not, R.isNil, R.prop('type'))),
            //     R.pathOr([], ['config', 'widgets'])
            // )(view);

            // view = R.assocPath(['config', 'widgets'], refinedWidgets, view);

            const promise =
                type === STORAGE_TYPES.STORE ||
                state.isShareMode === true ||
                state.ignoreSave === true
                    ? Promise.resolve()
                    : DashboardService.saveView({ view, userAccountId })
                          .then(() => {
                              const renderNotification = !silent && !isAutoSave;
                              const isEmailAutomationEnabled = R.path([
                                  'printPDF',
                                  'reportEmailAutomation',
                                  'enabled',
                              ])(rootState);
                              const isShowPDFWillUpdateNoticePopupOpened =
                                  R.path([
                                      'printPDF',
                                      'isShowPDFWillUpdateNoticePopupOpened',
                                  ])(rootState);

                              if (
                                  isEmailAutomationEnabled &&
                                  !isShowPDFWillUpdateNoticePopupOpened
                              ) {
                                  commit(
                                      'printPDF/setShowPDFWillUpdateNoticePopup',
                                      true,
                                      { root: true }
                                  );
                              }

                              renderNotification &&
                                  commit(
                                      ADD_NOTI,
                                      { type: NOTI_SUCCESS },
                                      { root: true }
                                  );

                              // Enable editing & focus if widget added is a text widget
                              if (
                                  widget &&
                                  R.equals(WIDGET_TYPES.NOTEPAD, widget.type)
                              ) {
                                  commit(
                                      'notepadWidget/setNotepadWidgetsEditingActiveStatus',
                                      {
                                          widgetId: widget.id,
                                          flag: true,
                                      },
                                      { root: true }
                                  );
                              }
                          })
                          .catch(() => {
                              commit(
                                  ADD_NOTI,
                                  { type: NOTI_FAIL },
                                  { root: true }
                              );
                          });

            return promise.then(() => {
                commit('updateView', view);

                if (type === STORAGE_TYPES.SERVER) {
                    // update origin views
                    const _view = getters.viewById(view.id);
                    commit(
                        'setOriginViews',
                        R.assoc(view.id, _view, state.originViews)
                    );
                }

                return view;
            });
        },
        changeViewOrders({ commit }, { newIndex, oldIndex, views = [] }) {
            let newViews = R.clone(views);
            newViews = changeOrder({ newIndex, oldIndex, value: views });
            newViews.forEach((view, idx) => (view.order = idx));

            // update optimistic way
            commit('setViews', newViews);

            const orders = newViews.map(R.prop('id')).map(Number);

            DashboardService.changeViewOrders(orders).catch((e) => {
                // restore optimistic update when failed
                console.log(e);
                commit('setViews', views);
            });
        },
        saveWidget(
            { dispatch, getters },
            { widget, type = STORAGE_TYPES.STORE, silent = false }
        ) {
            const { currentView: view } = getters;
            const updated = setWidgetInsideView({ view, widget });

            return dispatch('saveView', { view: updated, type, silent });
        },
        addWidget(
            { dispatch, getters, state, rootState },
            {
                widget,
                index = 0,
                type = WIDGET_ADD_TYPE.ADD_TO_TOP,
                pageGridId,
                widgetCopied,
            }
        ) {
            widget = addIdUnless(widget);

            const { currentView: view, currentViewConfig } = getters;

            // copy widget content if this operation is duplicating a text widget
            const isNotepadWidgetDuplication =
                type === WIDGET_ADD_TYPE.DUPLICATION &&
                WIDGET_TYPES.NOTEPAD === R.prop('type', widget);

            if (isNotepadWidgetDuplication) {
                const contentOfWidgetCopied = R.pathOr('', [
                    'notepadWidget',
                    'notepadWidgetsContent',
                    widgetCopied.id,
                    'content',
                ])(rootState);

                dispatch(
                    'notepadWidget/updateNotepadWidget',
                    {
                        widgetId: widget.id,
                        content: contentOfWidgetCopied,
                        viewId: view.id,
                    },
                    { root: true }
                );
            }

            const widgets = R.compose(
                R.insert(index, widget),
                R.defaultTo([]),
                R.prop('widgets')
            )(currentViewConfig);

            const isReportMode = R.path(['printPDF', 'isReportMode'])(
                rootState
            );

            // take care of current viewLayout update depending on report mode
            let newView = view;
            let currentLayout = [];
            if (isReportMode) {
                currentLayout = R.pathOr(
                    [],
                    ['config', 'reportModeLayout', pageGridId, 'layout']
                )(view);
            } else {
                currentLayout = R.pathOr(
                    [],
                    ['config', 'viewLayout', 'layout']
                )(newView);
            }

            /**
             * @note to avoid exposing "save button"
             */
            let refinedLayout = refineViewLayout({
                currentLayout,
                index,
                widgets,
                type,
            });

            if (isReportMode) {
                newView = getUpdateViewSyncedFromReportToDashboard(
                    newView,
                    refinedLayout,
                    widgets,
                    pageGridId
                );
                refinedLayout = R.pathOr(
                    [],
                    ['config', 'viewLayout', 'layout']
                )(newView);
            }

            newView = getUpdateViewSyncedFromDashboardToReportMode(
                newView,
                refinedLayout,
                widgets
            );

            return dispatch('saveView', {
                view: newView,
                type: STORAGE_TYPES.SERVER,
            }).then(() => state.widgetAdded$.next({ widget, index }));
        },
        changeWidgetOrders({ dispatch, getters }, { newIndex, oldIndex }) {
            const { currentView: view } = getters;

            let widgets = [...view.config.widgets];
            widgets = changeOrder({ newIndex, oldIndex, value: widgets });
            const newView = R.assocPath(['config', 'widgets'], widgets, view);

            dispatch('saveView', { view: newView, type: STORAGE_TYPES.STORE });
        },
        removeWidget({ dispatch, getters, rootState }, { widget, pageGridId }) {
            const { currentView: view } = getters;

            const widgets = R.reject(
                R.propEq('id', widget.id),
                view.config.widgets
            );

            const isReportMode = R.path(['printPDF', 'isReportMode'])(
                rootState
            );

            let newView = view;
            let currentLayout = [];

            if (isReportMode) {
                currentLayout = R.pathOr(
                    [],
                    ['config', 'reportModeLayout', pageGridId, 'layout']
                )(view);
            } else {
                currentLayout = R.pathOr(
                    [],
                    ['config', 'viewLayout', 'layout']
                )(view);
            }

            let updatedLayout = R.reject(
                R.propEq('i', widget.id),
                currentLayout
            );

            if (widgets.length === 0) {
                updatedLayout = [];
            }

            if (isReportMode) {
                newView = getUpdateViewSyncedFromReportToDashboard(
                    newView,
                    updatedLayout,
                    widgets,
                    pageGridId
                );
            } else {
                newView = getUpdateViewSyncedFromDashboardToReportMode(
                    newView,
                    updatedLayout,
                    widgets
                );
            }

            dispatch('saveView', {
                view: newView,
                type: STORAGE_TYPES.SERVER,
            });
        },
        getTableAdAccounts(
            { commit, rootGetters },
            { client, promiseToWait = Promise.resolve() } = {}
        ) {
            const dashboardClient =
                client || rootGetters['user/dashboardClient'];

            return dashboardClient
                .getAdAccounts()
                .then((v) => promiseToWait.then(() => v))
                .then((adAccounts = []) => {
                    const refined = R.uniq(adAccounts);

                    commit('setTableAdAccounts', refined);
                    return refined;
                })
                .catch(console.error);
        },
        async getTableAdAccountsV2({ commit }) {
            try {
                const data = await DashboardService.getDataSourcesV3();
                commit('setDataSources', { dataSources: data });
            } catch (e) {
                console.log('getTableAdAccountsV2: ERROR: ', e);
            }
        },
        resetViewById({ dispatch, getters }, id) {
            if (!id) {
                return;
            }

            const { originViewById, viewById } = getters;

            const oldView = originViewById(id);
            const cView = viewById(id) || {};

            // maintain old viewTitle
            const view = {
                ...oldView,
                ...R.pick(['viewTitle', 'view_title'], cView),
            };

            dispatch('saveView', { view });
        },
        saveCurrentViewToServer({ getters, dispatch }, param = {}) {
            const { isAutoSave = false } = param;

            return dispatch('saveView', {
                view: getters.currentView,
                type: STORAGE_TYPES.SERVER,
                isAutoSave,
            });
        },
        saveWidgetTitleToServer({ rootGetters, getters }, { view }) {
            if (!view.id) {
                return;
            }

            // find old view
            let oldView = getters.originViewsById[view.id];

            // widget_title updated to current view
            const titleById = R.compose(
                (map) => (id) => map[id] || '',
                R.map(R.prop('widget_title')),
                indexById
            )(view.config.widgets);

            oldView = R.over(
                R.lensPath(['config', 'widgets']),
                R.compose(
                    R.map((widget) => {
                        return R.assoc(
                            'widget_title',
                            titleById(widget.id),
                            widget
                        );
                    }),
                    R.defaultTo([])
                ),
                oldView
            );

            const userAccountId = rootGetters['user/userAccountId'];

            return DashboardService.saveView({
                view: oldView,
                userAccountId,
            }).catch((err) => {
                console.log({ err });
            });
        },
        updateFolderPermissions(
            { state, commit },
            { isPrivate, userIds, pendingUserEmails, teamIds, id }
        ) {
            return Promise.resolve()
                .then(() => {
                    return DashboardService.updateViewPermissions({
                        id,
                        isPrivate,
                        userIds,
                        pendingUserEmails,
                        teamIds,
                    });
                })
                .then(R.compose(attachPermissions, refineView))
                .then((folder) => {
                    let { originViews, views } = state;

                    const newOrigins = R.compose(
                        indexById,
                        FLATTEN_VIEWS
                    )(folder);

                    views = views.map(_replace);

                    commit('setOriginViews', {
                        ...originViews,
                        ...newOrigins,
                    });

                    commit('setViews', views);

                    function _replace(v) {
                        const viewId = v.id;
                        let view = { ...v };
                        const origin = newOrigins[viewId];

                        if (origin) {
                            view = {
                                ...origin,
                                $folded: v['$folded'],
                                order: v.order,
                            };
                        }

                        if (isValueExist(view.children)) {
                            view.children = view.children.map(_replace);
                        }

                        return view;
                    }
                })
                .then(() => {
                    // commit(ADD_NOTI, { type: NOTI_SUCCESS }, { root: true });
                })
                .catch(() => {
                    commit(ADD_NOTI, { type: NOTI_FAIL }, { root: true });
                });
        },
        selectByViewId({ commit }, { views = [], viewId }) {
            const _views = FLATTEN_VIEWS(views).filter(isView);
            const oldView = _views.find(({ id }) => id == viewId);

            commit(
                'setSelectedTab',
                oldView ? viewId : addTemplateOrFirst(_views)
            );
        },
        getFieldPreferences({ commit }) {
            return DashboardService.getFieldPreferences()
                .then((metricAliases = []) => {
                    commit('setMetricAliases', metricAliases);
                    return metricAliases;
                })
                .catch(console.error);
        },
        getCustomMetrics({ commit }, viewId) {
            return DashboardService.getCustomMetrics(viewId)
                .then((customMetricsByScope = {}) => {
                    const metrics = R.compose(
                        R.sortBy(R.compose(R.toLower, R.prop('name'))),
                        R.flatten,
                        R.values
                    )(customMetricsByScope);

                    commit('setCustomMetrics', metrics);

                    return metrics;
                })
                .catch(console.error);
        },
        updateBothViewSilently({ commit, state }, view) {
            if (!isValueExist(view)) {
                return;
            }
            commit('setOriginViews', R.assoc(view.id, view, state.originViews));
            commit('updateView', view);
        },
        saveViewToServerWithOriginData(
            { getters, rootGetters, commit, state },
            view = {}
        ) {
            const { id: viewId } = view;

            if (!viewId) {
                return;
            }

            const { originViewById } = getters;

            const userAccountId = rootGetters['user/userAccountId'];

            let newView = R.mergeDeepLeft(view, originViewById(viewId));

            return DashboardService.saveView({
                view: newView,
                userAccountId,
            }).then(() => {
                commit(
                    'setOriginViews',
                    R.assoc(viewId, newView, state.originViews)
                );
            });
        },
    },
    getters: {
        dataSourcesDisconnectedWithin7Days(state) {
            const filteredDataSources = R.compose(
                R.uniqBy(R.path(['properties', 'dataSource', 'id'])),
                R.reduce((acc, curr) => {
                    let { type, asset } = curr;

                    const parsedAsset = {
                        code: CONNECTION_LOST,
                        type: ERROR_TYPE.WARNING,
                        message: 'DATA SOURCE disconnected',
                        properties: {
                            connectorType: type,
                            dataSource: asset,
                        },
                    };

                    acc.push(parsedAsset);
                    return acc;
                }, []),
                R.filter(
                    R.pathSatisfies(
                        (disableAt) => {
                            const diff = moment().diff(
                                moment(disableAt),
                                'days'
                            ); // exclude start
                            return diff < 7;
                        },
                        ['asset', 'disableAt']
                    )
                ),
                R.filter(
                    R.pathSatisfies(
                        (disableAt) =>
                            disableAt !== null && disableAt !== undefined,
                        ['asset', 'disableAt']
                    )
                ),
                R.filter(
                    R.pathSatisfies(
                        (connected) => connected === false,
                        ['asset', 'connected']
                    )
                )
            )(state.dataSources);

            return filteredDataSources;
        },
        pdfReportInfo: (state, getters) => {
            const { currentViewConfig } = getters;
            return R.prop('pdfReportInfo')(currentViewConfig) || {};
        },
        hasNoAdAccountsConnected: (state) => {
            return state.tableAccount && !isValueExist(state.tableAdAccounts);
        },
        campaigns: R.prop('campaigns'),
        campaignId: R.prop('campaignId'),
        dimensionIds(_, { currentDimension }) {
            return currentDimension.map((value) => value.id || value);
        },
        dimensionById: R.compose(indexById, R.propOr([], 'dimensions')),
        viewsById: R.compose(indexById, FLATTEN_VIEWS, R.propOr([], 'views')),
        viewById(_, { viewsById }) {
            return (id) => viewsById[id] || {};
        },
        originViewsById: R.propOr({}, 'originViews'),
        originViewById(_, { originViewsById }) {
            return (id) => originViewsById[id] || {};
        },
        currentView(state, getters) {
            const { selectedTab } = state;
            return getters.viewById(selectedTab);
        },
        currentViewConfig: memoize(
            R.compose(
                Object.freeze,
                R.pathOr({}, ['currentView', 'config']),
                R.nthArg(1)
            ),
            R.compose(
                JSON.stringify,
                R.pathOr({}, ['currentView', 'config']),
                R.nthArg(1)
            )
        ),
        currentViewFilter: (function () {
            let cache = defaultToFrozenEmptyObj(undefined);

            const func = R.compose(
                defaultToFrozenEmptyObj,
                parseTagsToParam,
                R.objOf('filters'),
                R.pathOr([], ['currentViewConfig', 'filterTagList'])
            );

            return (_, getters) => {
                const nextValue = func(getters);

                if (notEq(nextValue, cache)) {
                    cache = nextValue;
                }

                return cache;
            };
        })(),
        dateFilterProps: memoize(
            function (_, { currentViewConfig: newConfig }) {
                if (!isValueExist(newConfig)) {
                    const [from, to] = getDefaultDate();
                    const dateFilter = { from, to };
                    const mixedDateFilter = {
                        ...dateFilter,
                        ...VIEW_DEFAULT_DATE_FILTR,
                    };

                    return {
                        dateFilter,
                        mixedDateFilter,
                        comparisonDateFilter: {},
                    };
                }

                const payload = R.pick(
                    ['dateFilter', 'comparisonDateFilter'],
                    newConfig
                );

                const { dateFilter, comparisonDateFilter } = payload;

                if (isAdvancedDateFilter(dateFilter)) {
                    return {
                        rawDateFilter: dateFilter,
                        dateFilter: getAdvancedDateFilter({ dateFilter }),
                        rawComparisonDateFilter: comparisonDateFilter,
                        comparisonDateFilter: isAdvancedDateFilter(
                            comparisonDateFilter
                        )
                            ? getAdvancedDateFilter({
                                  dateFilter: comparisonDateFilter,
                              })
                            : comparisonDateFilter,
                    };
                }

                const { type, values, todayExcluded = true } = dateFilter;
                const dateFilterValue = R.pathOr('', [type, 'value'], values);

                /**
                 * Date Filter
                 */
                const date = R.equals(type, 'relative')
                    ? convertRelativeDate(values[type])
                    : values[type];

                const { dateFilter: parsedDateFilter, dateFilterStatus } =
                    getDateFilter({
                        date,
                        todayExcluded,
                        dateFilterValue,
                        savedValue: values[type],
                    });

                const savedComprisonDate = R.pathOr(
                    {},
                    ['values', 'range', 'period'],
                    comparisonDateFilter
                );

                /**
                 * Comparison Date Filter
                 */
                const comparisonType = R.pathOr(
                    'relative',
                    ['type'],
                    comparisonDateFilter
                );
                const savedComprisonPeriod = R.pathOr(
                    '',
                    ['values', comparisonType, 'period'],
                    comparisonDateFilter
                );

                const {
                    comparisonDateFilter: parsedComparisonDateFilter,
                    comparisonDateStatus: parsedComparisonDateStatus,
                } = getComparisonDateFilter({
                    date:
                        comparisonType === 'relative' ? {} : savedComprisonDate,
                    comparedDate: parsedDateFilter,
                    period: savedComprisonPeriod,
                    dateFilterStatus,
                });

                return Object.freeze({
                    rawDateFilter: dateFilter,
                    dateFilter: parsedDateFilter,
                    dateFilterStatus,
                    todayExcluded,
                    rawComparisonDateFilter: comparisonDateFilter,
                    comparisonDateFilter: parsedComparisonDateFilter,
                    comparisonDateStatus: parsedComparisonDateStatus,
                    mixedDateFilter: { ...parsedDateFilter, ...dateFilter },
                    mixedComparisonDateFilter: {
                        ...parsedComparisonDateFilter,
                        ...comparisonDateFilter,
                    },
                });
            },
            (_, { currentViewConfig: config = {} }) => {
                const payload = R.pick(
                    ['dateFilter', 'comparisonDateFilter'],
                    config
                );
                return JSON.stringify(payload);
            }
        ),
        dateUnit(_, getters) {
            // day, week, month
            const { dateFilter } = getters.dateFilterProps;
            return getDateGroupUnit(dateFilter);
        },
        currentDimension(state) {
            const { dimensions } = state;
            let rows = R.path(['editingWidget', 'dimensionIds'], state);

            const values = makeDimension(dimensions, rows);
            return !isValueExist(values) ? [] : values;
        },
        currentWidgets: (function () {
            let cache;

            return (_, getters) => {
                const value = R.compose(
                    R.pathOr([], ['config', 'widgets']),
                    R.propOr([], 'currentView')
                )(getters);

                if (notEq(cache, value)) {
                    cache = value;
                }

                return cache;
            };
        })(),
        hasWidgets: (_, { currentWidgets }) => {
            return isValueExist(currentWidgets);
        },
        currentWidgetsById: R.compose(
            indexById,
            R.propOr([], 'currentWidgets'),
            R.nthArg(1)
        ),
        editingWidgetId: R.path(['editingWidget', 'id']),
        editingChildWidgetId: R.pathOr('', ['editingWidget', 'childId']),
        isResizableDisabled: (() => {
            const func = R.compose(
                R.pathOr(false, ['viewLayout', 'disabled']),
                R.propOr({}, 'currentViewConfig'),
                R.nthArg(1)
            );

            return memoize(func, func);
        })(),
        viewLayout: memoize(
            R.compose(
                Object.freeze,
                R.pathOr({}, ['currentViewConfig', 'viewLayout']),
                R.nthArg(1)
            ),
            R.compose(
                JSON.stringify,
                R.pathOr({}, ['currentViewConfig', 'viewLayout']),
                R.nthArg(1)
            )
        ),
        isCurrentViewChanged(_, getters, _2, rootGetters) {
            const user = rootGetters['user/user'];

            if (!user) {
                return false;
            }

            const id = R.path(['currentView', 'id'], getters);
            const omit = R.compose(
                // R.dissocPath(['config', 'viewLayout', 'layout']),
                R.dissocPath(['config', 'reportModeLayout']),
                R.dissocPath(['logoMedia']),
                R.over(
                    R.lensPath(['config', 'widgets']),
                    R.compose(R.map(R.dissocPath(['page'])), R.defaultTo([]))
                ),
                R.over(
                    R.lensPath(['config', 'widgets']),
                    R.compose(
                        R.map(
                            R.compose(
                                R.evolve({
                                    adAccounts: R.compose(R.pluck('channelId')),
                                }),
                                R.omit(['widget_title', 'updatedAt'])
                            )
                        ),
                        R.defaultTo([])
                    )
                ),
                R.omit([
                    'order',
                    'view_title',
                    'viewTitle',
                    '$folded',
                    '$droppable',
                    'path',
                    'isDefault',
                    'isViewable',
                    'isPrivate',
                ])
            );

            let originView = getters.originViewById(id);
            let renderView = getters.viewById(id);

            const _refine = R.compose(omit, refineView);

            originView = _refine(originView);
            renderView = _refine(renderView);

            /**
             * Exposing saving btn test
             */
            // const _pathOr = R.pathOr([], ['config', 'viewLayout', 'layout']);
            // const _pathOr = R.pathOr([], ['config', 'widgets']);
            // const isEqualTest = R.equals(
            //     _pathOr(originView),
            //     _pathOr(renderView)
            // );

            // if (!isEqualTest) {
            //     const _viewOrigin = _pathOr(originView);
            //     const _viewRender = _pathOr(renderView);

            //     _viewOrigin.forEach((_, index) => {
            //         const isEqual = R.equals(_viewOrigin, _viewRender);

            //         if (!isEqual) {
            //             console.log('_viewOrigin', _viewOrigin[index]);
            //             console.log('_viewRender', _viewRender[index]);
            //         }
            //     });
            // }

            return !R.equals(originView, renderView);
        },
        leftNavWidth(state) {
            const { leftNavWidth, leftNavFolded } = state;

            if (!leftNavFolded) {
                let width = parseFloat(leftNavWidth || 232);

                return toPixel(leftClamp(width));
            } else {
                return toPixel(0);
            }
        },
        getWidgetIdx(state, getters) {
            const { currentWidgets = [] } = getters;

            return ({ id } = {}) =>
                R.findIndex(R.propEq('id', id), currentWidgets);
        },
        adAccounts(state) {
            const { tableAdAccounts = [] } = state;
            return tableAdAccounts.filter(
                R.compose(R.not, checkIsAnalyticSource),
                tableAdAccounts
            );
        },
        analyticAccounts: R.compose(
            R.filter(checkIsAnalyticSource),
            R.propOr([], 'tableAdAccounts')
        ),
        adsTableSources: R.compose(
            R.reject(checkIsAnalyticSource),
            R.propOr([], 'tableAdAccounts')
        ),
        customMetricsMap: ({ customMetrics }) => {
            return R.indexBy(R.prop('key'), customMetrics);
        },
        customMetricsConverted({ customMetrics }) {
            return customMetrics.reduce((acc, { key, name }) => {
                acc[key] = name;
                return acc;
            }, {});
        },
        adAccountChannels(state) {
            const { tableAdAccounts = [] } = state;

            const adAccountChannelSet = new Set();

            if (tableAdAccounts)
                tableAdAccounts.forEach((account) => {
                    const channel = refineChannel(account);

                    adAccountChannelSet.add(channel);
                });
            else {
                return [];
            }

            return [...adAccountChannelSet];
        },
        getCellType: ({ customMetrics = [], metricTypeMap = {} }) => {
            const map = R.indexBy(R.prop('key'), customMetrics);

            return (name = '') => {
                return (
                    R.path([name, 'type'], map) ||
                    getCellType(name) ||
                    (() => {
                        const beType = metricTypeMap[name];
                        return beType === 'any' ? 'string' : beType;
                    })() ||
                    'string'
                );
            };
        },
        tableAdAccountsByAlternativeChannelName: R.compose(
            (tableAdAccounts) => {
                return tableAdAccounts.reduce((acc, account) => {
                    const refinedChannel = refineChannel(account);
                    acc[refinedChannel] = account;
                    return acc;
                }, {});
            },
            R.propOr([], 'tableAdAccounts')
        ),
        tableAdAccountsByChannel: R.compose(
            R.indexBy(R.prop('channel')),
            R.propOr([], 'tableAdAccounts')
        ),
        tableAdAccountsByChannelId: R.compose(
            R.indexBy(R.prop('channelId')),
            R.propOr([], 'tableAdAccounts')
        ),
        tableAdAccountsByGroupId: R.compose(
            R.groupBy(R.prop('groupId')),
            R.propOr([], 'tableAdAccounts')
        ),
        tableAdAccountsByAccountKey: R.compose(
            R.indexBy(getAccountKey),
            R.propOr([], 'tableAdAccounts')
        ),
        tableAdAccountsByDisplayChannelNameChannelId: R.compose(
            R.indexBy(getAccountKeyWithDisplayChannelName),
            R.propOr([], 'tableAdAccounts')
        ),
        getMergedFilter: memoize(function (_, { currentViewFilter }) {
            return (widgetFilter) =>
                getMergedDashboardFilter(
                    clearEmptyFilter(currentViewFilter),
                    clearEmptyFilter(widgetFilter)
                );
        }, R.compose(
            JSON.stringify,
            R.propOr({}, 'currentViewFilter'),
            R.nthArg(1)
        )),
        customMetricKeys: R.compose(
            R.pluck('key'),
            R.prop('customMetrics'),
            R.nthArg(0)
        ),
        viewsPathMap: (function () {
            let cached;

            return (state) => {
                const newValue = R.compose(
                    Object.freeze,
                    VIEW_PATH_MAP,
                    R.objOf('data'),
                    R.propOr([], 'views')
                )(state);

                if (notEq(cached, newValue)) {
                    cached = newValue;
                }

                return cached;
            };
        })(),
        accFilters: (function () {
            let cached;

            return (state, getters) => {
                const { views = [] } = state;
                const { currentView: { id } = {}, viewsPathMap: pathMap } =
                    getters;
                let paths = pathMap[id] || [];

                // const viewFilter = R.view(
                //     R.lensPath(['config', 'filterTagList'])
                // );
                const viewWhere = R.view(R.lensPath(['config', 'where']));

                const interChildren = R.intersperse('children');

                let acc = R.times(R.identity, paths.length - 1).reduce(
                    (acc, _, idx) => {
                        let path = R.take(idx + 1, paths);
                        path = interChildren(path);

                        const view = R.path(path, views) || {};

                        // let tagList = viewFilter(view) || [];
                        // const list = tagList.filter(R.prop('filterBy'));

                        const where = viewWhere(view);
                        const filterTagList = makeFilterFromFolderWhere(where);

                        if (isFolder(view) && isValueExist(filterTagList)) {
                            acc.push(...filterTagList);
                        }

                        return acc;
                    },
                    []
                );

                if (notEq(acc, cached)) {
                    acc = Object.freeze(acc);
                    cached = acc;
                }

                return cached;
            };
        })(),
        layoutMap: (function () {
            let cache = EMPTY_OBJ;

            return (_, getters) => {
                let nextValue = R.compose(
                    R.indexBy(R.prop('i')),
                    R.pathOr([], ['viewLayout', 'layout'])
                )(getters);

                if (!isValueExist(nextValue)) {
                    nextValue = EMPTY_OBJ;
                }

                if (notEq(nextValue, cache)) {
                    cache = nextValue;
                }

                return cache;
            };
        })(),
        viewsLength: memoize(
            R.pathOr(0, ['views', 'length']),
            R.pathOr(0, ['views', 'length'])
        ),
        onlyAdDataSources: R.compose(
            R.filter(R.prop('isAdAccount')),
            R.propOr([], 'tableAdAccounts')
        ),
        renderWidgetStatusPanel: R.compose(
            R.not,
            R.isNil,
            R.prop('widgetStatusPanelWidgetID')
        ),
        sortedLayoutIds: R.compose(
            R.map(R.nth(0)),
            R.sortBy(R.path([1, 'y'])),
            Object.entries,
            R.defaultTo({}),
            R.prop('layoutMap'),
            R.nthArg(1)
        ),
        accFolders: (function () {
            let cached;

            return (state, getters) => {
                const { views = [] } = state;
                const { currentView: { id } = {}, viewsPathMap: pathMap } =
                    getters;
                let paths = pathMap[id] || [];

                const interChildren = R.intersperse('children');

                let acc = R.times(R.identity, (paths.length || 1) - 1).reduce(
                    (acc, _, idx) => {
                        let path = R.take(idx + 1, paths);
                        path = interChildren(path);

                        const view = R.path(path, views) || {};

                        if (!checkIsView(view)) {
                            acc.push(view);
                        }

                        return acc;
                    },
                    []
                );

                if (notEq(acc, cached)) {
                    acc = Object.freeze(acc);
                    cached = acc;
                }

                return cached;
            };
        })(),
        isSharedViewInteractionBlocked: R.compose(
            R.propEq('sharableViewPermission', 'private'),
            R.prop('user/account'),
            R.nthArg(3)
        ),
        aliasesByIndex({ metricAliases }) {
            return metricAliases.reduce(
                (acc, { originalFieldName, displayFieldName, id }) => {
                    acc[originalFieldName] = { displayFieldName, id };
                    return acc;
                },
                {}
            );
        },
        datasourceMapByAssetId: R.compose(
            R.indexBy(R.path(['asset', 'assetId'])),
            R.propOr([], 'dataSources')
        ),
    },
};

function attachPermissions(view = {}) {
    const { isViewable, isPrivate } = view;

    const attachPermissions = R.mergeLeft({ isViewable, isPrivate });

    return R.when(
        isFolder,
        R.over(R.lensProp('children'), R.map(attachPermissions))
    )(view);
}

function refineView(view = {}) {
    return R.compose(
        R.ifElse(
            checkIsView,
            R.dissoc('children'),
            R.over(
                R.lensProp('children'),
                R.unless(Array.isArray, () => [])
            )
        ),
        R.over(
            R.lensPath(['config', 'filterTagList']),
            R.compose(
                R.map(R.omit(['operator', 'searchByText'])),
                R.defaultTo([])
            )
        ),
        R.over(
            R.lensPath(['config', 'widgets']),
            R.compose(
                Object.freeze,
                R.map(
                    R.compose(
                        R.when(
                            R.allPass([
                                R.propEq('type', 'pivotTable'),
                                R.propSatisfies(R.isNil, 'sort'),
                            ]),
                            (widget) => {
                                const firstDimension = R.path(
                                    ['dimensionIds', 0],
                                    widget
                                );

                                const isDateGroupRow =
                                    DATE_GROUP_ROWS.includes(firstDimension);

                                const name = (() => {
                                    if (firstDimension === 'keyword') {
                                        return 'spend';
                                    } else if (isDateGroupRow) {
                                        return 'name';
                                    } else {
                                        return 'clicks';
                                    }
                                })();

                                return R.assoc(
                                    'sort',
                                    {
                                        name,
                                        isDesc: isDateGroupRow ? false : true,
                                    },
                                    widget
                                );
                            }
                        ),
                        R.when(
                            R.propSatisfies(R.equals(null), 'adAccounts'),
                            R.assoc('adAccounts', [])
                        ),
                        R.when(
                            R.allPass([
                                R.propEq('type', 'pivotTable'),
                                R.propSatisfies(R.isNil, 'barColumns'),
                            ]),
                            R.assoc('barColumns', ['clicks', 'cpc'])
                        ),
                        // dimensionIds는 string|number[] 여야 하지만
                        // 버그로 인해 object가 저장된 경우 id를 추출해 정규화
                        R.over(
                            R.lensProp('dimensionIds'),
                            (ids = []) =>
                                ids.map((d) =>
                                    d !== null && typeof d === 'object'
                                        ? d.id
                                        : d
                                )
                        )
                    )
                ),
                R.reject(R.propEq('type', 'table')),
                R.defaultTo([])
            )
        ),
        // fill default dateFilter
        R.over(
            R.lensPath(['config', 'dateFilter']),
            R.compose(
                R.ifElse(
                    R.propEq('type', 'range'),
                    R.dissocPath(['values', 'relative']),
                    R.dissocPath(['values', 'range'])
                ),
                R.map(
                    // R.omit(['todayExcluded']),
                    R.defaultTo(VIEW_DEFAULT_DATE_FILTR)
                ),
                R.unless(isValueExist, R.always(VIEW_DEFAULT_DATE_FILTR))
            )
        ),
        // fill type if empty
        R.over(R.lensProp('type'), R.unless(isValueExist, R.always('view'))),
        R.dissocPath(['config', 'attributionWindows']),
        // casting id to String
        R.over(R.lensProp('id'), String)
    )(view);
}

function attachTeamspaceName(views = [], parentTeamspaceName = '') {
    return views.map((view) => {
        const isTeamspace = view.type === 'teamspace';
        const nextTeamspaceName =
            (isTeamspace && view.viewTitle) ||
            parentTeamspaceName ||
            R.path(['teamspace', 'name'], view) ||
            R.path(['teamspace', 'viewTitle'], view) ||
            '';

        const teamspace =
            view.teamspace || nextTeamspaceName
                ? {
                      ...view.teamspace,
                      name:
                          R.path(['teamspace', 'name'], view) ||
                          nextTeamspaceName ||
                          undefined,
                      viewTitle:
                          R.path(['teamspace', 'viewTitle'], view) ||
                          nextTeamspaceName ||
                          undefined,
                  }
                : view.teamspace;

        const children = R.when(Array.isArray, (c) =>
            attachTeamspaceName(c, nextTeamspaceName)
        )(view.children);

        return {
            ...view,
            ...(nextTeamspaceName ? { teamspaceName: nextTeamspaceName } : {}),
            ...(teamspace ? { teamspace } : {}),
            ...(children ? { children } : {}),
        };
    });
}

function refineViews(views = []) {
    return views
        .map(refineView)
        .map(
            R.when(
                R.complement(checkIsView),
                R.over(R.lensProp('children'), refineViews)
            )
        );
}

const overChildren = R.over(R.lensProp('children'));

function sortByOrder(views = []) {
    return R.compose(
        R.map(whenFolder(overChildren(sortByOrder))),
        R.sortBy(R.prop('order'))
    )(views);
}

const shouldBeRefinedLabelKeys = ['campaignIds', 'adSetIds', 'adIds'];

const refineDimensions = (dimensions) => {
    return R.map(
        R.over(
            R.lensProp('labels'),
            R.map(
                R.over(R.lensProp('dashboardFilter'), (filter) => {
                    filter = filter || {};
                    shouldBeRefinedLabelKeys.forEach((key) => {
                        const value = filter[key];

                        if (isValueExist(value)) {
                            filter = R.dissoc(key, filter);

                            const existedIds = filter['ids'] || [];

                            filter = R.assoc(
                                'ids',
                                R.uniq([
                                    ...existedIds,
                                    ...value.map((id) => ({
                                        level: R.replace('Ids', '', key),
                                        value: refineFullId(id),
                                    })),
                                ]),
                                filter
                            );
                        }
                    });

                    return filter;
                })
            )
        ),
        dimensions
    );
};

const refineFullId = (id) => {
    let refined = String(id);
    const splitted = R.split(':', id);

    if (isValueExist(splitted)) {
        refined = R.last(splitted);
    }

    return refined;
};
